// The keypad modal's input mechanics: the four widget layouts, plain-digit
// entry, dart-tally entry (accumulating taps against a fixed per-dart value
// until submit), and the binary hit/miss widget. This module only ever
// touches the display element it's handed - it knows nothing about rounds,
// players, or what happens when a value is submitted; callers own that via
// their own submit handler.
//
// Like core/menu.js, entry state (first-input flag, dart tally) is
// module-scoped rather than passed around, since only one keypad instance is
// ever open at a time. Call resetKeypadEntryState() whenever a keypad opens.

let firstInput = true;
let dartsTally = [];
let dartHalved = false;

export function resetKeypadEntryState() {
    firstInput = true;
    dartsTally = [];
    dartHalved = false;
}

export function getNumericKeypadHTML() {
    return `
        <button class="keypad-btn" onclick="keypadInput('1')">1</button>
        <button class="keypad-btn" onclick="keypadInput('2')">2</button>
        <button class="keypad-btn" onclick="keypadInput('3')">3</button>
        <button class="keypad-btn" onclick="keypadInput('4')">4</button>
        <button class="keypad-btn" onclick="keypadInput('5')">5</button>
        <button class="keypad-btn" onclick="keypadInput('6')">6</button>
        <button class="keypad-btn" onclick="keypadInput('7')">7</button>
        <button class="keypad-btn" onclick="keypadInput('8')">8</button>
        <button class="keypad-btn" onclick="keypadInput('9')">9</button>
        <button class="keypad-btn utility utility-numeric" onclick="keypadClear()" title="Clear" aria-label="Clear"><i class="fa-solid fa-trash-can"></i></button>
        <button class="keypad-btn" onclick="keypadInput('0')">0</button>
        <button class="keypad-btn utility utility-numeric" onclick="keypadBackspace()" title="Backspace" aria-label="Backspace"><i class="fa-solid fa-delete-left"></i></button>
        <button class="keypad-btn success wide" onclick="keypadSubmit()" title="Enter" aria-label="Enter"><i class="fa-solid fa-check"></i></button>
    `;
}

export function getDartKeypadHTML() {
    return `
        <div style="grid-column: span 3;">
            <div id="dartboardMount" class="dartboard-mount"></div>
            <div style="display:grid; grid-template-columns: repeat(3, 1fr); gap:8px; margin-top:12px;">
                <button class="keypad-btn utility" onclick="dartClear()" title="Clear" aria-label="Clear"><i class="fa-solid fa-trash-can"></i></button>
                <button class="keypad-btn danger" onclick="dartHalve()" title="Miss" aria-label="Miss"><i class="fa-solid fa-circle-xmark"></i></button>
                <button class="keypad-btn utility" onclick="dartUndo()" title="Backspace" aria-label="Backspace"><i class="fa-solid fa-delete-left"></i></button>
            </div>
            <button class="keypad-btn success" style="width:100%; margin-top:8px;" onclick="keypadSubmit()" title="Enter" aria-label="Enter"><i class="fa-solid fa-check"></i></button>
        </div>
    `;
}

export function getBullKeypadHTML() {
    // Same layout as the numbered-wedge widget - only the dartboard mount's
    // content differs (bull zoom vs single-wedge zoom), wired by the caller.
    return getDartKeypadHTML();
}

export function getFortyOneKeypadHTML() {
    return `
        <div style="grid-column: span 3;">
            <button class="keypad-btn success" style="width:100%; height:100px; font-size:2.2rem;" onclick="fortyOneHit()">41</button>
            <div style="display:grid; grid-template-columns: repeat(3, 1fr); gap:8px; margin-top:12px;">
                <button class="keypad-btn utility" onclick="keypadClear()" title="Clear" aria-label="Clear"><i class="fa-solid fa-trash-can"></i></button>
                <button class="keypad-btn danger" onclick="fortyOneMiss()" title="Miss" aria-label="Miss"><i class="fa-solid fa-circle-xmark"></i></button>
                <button class="keypad-btn utility" onclick="keypadBackspace()" title="Backspace" aria-label="Backspace"><i class="fa-solid fa-delete-left"></i></button>
            </div>
            <button class="keypad-btn success" style="width:100%; margin-top:8px;" onclick="keypadSubmit()" title="Enter" aria-label="Enter"><i class="fa-solid fa-check"></i></button>
        </div>
    `;
}

export function digitInput(displayEl, num) {
    // If this is the first input, replace the existing value
    if (firstInput) {
        displayEl.textContent = num;
        firstInput = false;
    } else {
        // Otherwise append to the current value
        if (displayEl.textContent === '' || displayEl.textContent === '0') {
            displayEl.textContent = num;
        } else if (displayEl.textContent.length < 3) {
            displayEl.textContent += num;
        }
    }
}

export function digitClear(displayEl) {
    displayEl.textContent = '';
    firstInput = true;
}

export function digitBackspace(displayEl) {
    if (displayEl.textContent.length > 1) {
        displayEl.textContent = displayEl.textContent.slice(0, -1);
    } else {
        displayEl.textContent = '';
    }
}

function updateDartDisplay(displayEl, dartValue) {
    const total = dartsTally.reduce((sum, mult) => sum + mult * dartValue, 0);
    displayEl.textContent = dartsTally.length ? total : '';
}

// dartValue is the fixed per-dart point value (the round's target number, or
// 25 for bull) - the caller has already clamped `mult` to whatever the
// current widget allows (e.g. bull tops out at 2).
export function dartTap(displayEl, mult, dartValue) {
    if (dartHalved) {
        dartHalved = false;
        dartsTally = [];
    }
    dartsTally.push(mult);
    updateDartDisplay(displayEl, dartValue);
}

export function dartHalve(displayEl) {
    dartsTally = [];
    dartHalved = true;
    displayEl.textContent = '0';
}

export function dartUndo(displayEl, dartValue) {
    if (dartHalved) {
        dartHalved = false;
        displayEl.textContent = '';
        return;
    }
    dartsTally.pop();
    updateDartDisplay(displayEl, dartValue);
}

export function dartClear(displayEl) {
    dartsTally = [];
    dartHalved = false;
    displayEl.textContent = '';
}

export function fortyOneHit(displayEl) {
    displayEl.textContent = '41';
    firstInput = false;
}

export function fortyOneMiss(displayEl) {
    displayEl.textContent = '0';
    firstInput = false;
}
